<template>
  <div class="borderRadius pb-0 fobs_graph_container">
    <apexchart
      v-if="props.incidentFobs?.length"
      ref="fobsChart"
      :key="theme"
      class="fobs_chart"
      type="scatter"
      height="300"
      :options="chartOptions"
      :series="seriesData"
      @click="clickHandler"></apexchart>
    <div v-else style="display: flex !important; height: 300px !important" class="align-center justify-center">
      <div>
        {{ $t('messages.info.noFobsPoints') }}
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
// Settings
import settingsStore from '@/stores/settingsStore.ts'
const settings = settingsStore()
const { theme } = storeToRefs(settings)

//Framework import
import { computed, nextTick, onBeforeMount, ref, toRef, watch } from 'vue'

import { storeToRefs } from 'pinia'

import type { ApexOptions } from 'apexcharts'
import { toNumber } from 'lodash'
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
const { lang } = storeToRefs(langStore())

import langStore from '@/stores/langStore'
import type { IncidentFob, FobSeriesDataType, FobsPointData, ThemedColors } from '@/types/Fobs'

const emits = defineEmits<{ (event: 'selected', dataId: string): void }>()
const props = defineProps({
  incidentFobs: {
    type: Array<IncidentFob>,
    default: () => []
  }
})

const incidentFobs = toRef(props, 'incidentFobs')
const seriesData = ref([] as Array<FobSeriesDataType>)
// initalizeSeriesData()

const themedColors = computed((): ThemedColors => {
  return {
    markerStroke: theme.value === 'light' ? '#000' : '#fff',
    grid: theme.value === 'light' ? '#0f0f0f' : '#0f0f0f',
    xaxis: theme.value === 'light' ? '#000' : '#fff',
    xaxisTick: theme.value === 'light' ? '#000' : '#fff',
    yaxis: theme.value === 'light' ? '#000' : '#fff'
  } as ThemedColors
})
const chartColors = ref<string[]>([])

const chartOptions = computed((): ApexOptions => ({
  chart: {
    type: 'scatter',
    zoom: {
      enabled: false,
      type: 'xy'
    },
    toolbar: {
      show: false
    },
    offsetX: 0,
    offsetY: 0
  },
  markers: {
    strokeWidth: 1,
    size: 4,
    strokeColors: themedColors.value.markerStroke,
    hover: {
      size: 5.5
    },
    showNullDataPoints: false
  },
  legend: {
    show: true,
    showForSingleSeries: true,
    position: 'bottom',
    fontSize: '10px',
    offsetY: 5,
    labels: {
      colors: 'rgb(var(--v-theme-lighten))'
    },
    floating: false
  },
  dataLabels: {
    enabled: false
  },
  tooltip: {
    enabled: true,
    followCursor: false,
    shared: false,
    fillSeriesColor: false,
    theme: theme.value as 'light' | 'dark',
    style: {
      fontSize: '9px'
    },
    onDatasetHover: {
      highlightDataSeries: true
    },
    marker: {
      show: true
    },
    x: {
      show: true
    },
    y: {
      title: {
        formatter: undefined
      },
      formatter: undefined
    },
    z: {
      title: '',
      formatter: undefined
    }
  },
  grid: {
    borderColor: themedColors.value.grid,
    xaxis: {
      lines: {
        show: false
      }
    },
    yaxis: {
      lines: {
        show: true
      }
    }
  },
  xaxis: {
    type: 'datetime',
    labels: {
      show: true,
      style: {
        colors: themedColors.value.xaxis,
        fontSize: '8px'
      }
    },
    axisTicks: {
      show: true,
      borderType: 'solid',
      color: themedColors.value.xaxisTick
    }
  },
  yaxis: {
    show: true,
    tickAmount: 5,
    labels: {
      show: true,
      style: {
        colors: themedColors.value.yaxis,
        fontSize: '10px'
      },
      formatter: (value: number) => (value !== 0 ? String(value) : '')
    },
    min: 0,
    max: 5
  },
  colors: chartColors.value
}))

const clickHandler = (event: unknown, chartContext: unknown, config: { dataPointIndex: number; seriesIndex: number }) => {
  //if (config.dataPointIndex < 0 || config.seriesIndex < 0) {
  //  addNotification({
  //    type: "warning",
  //    title: t("messages.validation.chartWrongClick"),
  //    time: 1500,
  //  })
  //} else {
  //  const props = seriesData.value[config.seriesIndex].data[config.dataPointIndex][3]
  //  emits("selected", props.id)
  //}
  try {
    const point = seriesData.value[config.seriesIndex].data[config.dataPointIndex]
    if (point.length === 4) emits('selected', point[3].id)
  } catch (err) {
    // When a click on the table is not in a point, there is an error accessing
    //console.log(err)
  }
}

function fullFillFobSeriesData(fobs: Array<IncidentFob>) {
  fobs.forEach((element: IncidentFob) => {
    let spreadDataElement: FobSeriesDataType | undefined = seriesData.value.find(
      (serie) => serie.id == element.observationPreset.spreadType.id
    )

    if (spreadDataElement === undefined) {
      seriesData.value.push({
        name: '',
        data: [],
        id: element.observationPreset.spreadType.id,
        originalName: element.observationPreset.spreadType.name
      })
      chartColors.value.push(element.observationPreset.spreadType.color)
      spreadDataElement = seriesData.value[seriesData.value.length - 1]
    }

    spreadDataElement.data.push([
      new Date(element.created_at).getTime(),
      element.observationPreset.spreadType.displayOrder,
      element.observationPreset.spottingType.displayOrder,
      {
        id: element.id ?? ''
      }
    ])
  })
  if (seriesData.value.length) insertNullElements()
  updateLanguage()
}

function updateLanguage() {
  seriesData.value.forEach((element: FobSeriesDataType) => {
    if (element.originalName !== '') element.name = t('domain.enums.spreadType.' + element.originalName)
  })
  const spreadingTypesForTranslate = [
    'domain.enums.spreadType.smouldering',
    'domain.enums.spreadType.creeping_spreading',
    'domain.enums.spreadType.running_spotting_torching',
    'domain.enums.spreadType.crowning_spotting',
    'domain.enums.spreadType.extreme_erratic'
  ]
  const spreadingTypeLabel = 'views.fobs.spread'
  const tooltip = chartOptions.value.tooltip
  const tooltipY = Array.isArray(tooltip?.y) ? undefined : tooltip?.y
  if (tooltipY) {
    tooltipY.formatter = (value: number) => {
      const spreadingTypesTranslated = spreadingTypesForTranslate.map((x) => t(x))
      return spreadingTypesTranslated[value - 1]
    }
    if (tooltipY.title) {
      tooltipY.title.formatter = () => {
        return t(spreadingTypeLabel) + ':'
      }
    }
  }
  const spottingTypesForTranslate = [
    'domain.enums.spottingType.none',
    'domain.enums.spottingType.short_range',
    'domain.enums.spottingType.long_range'
  ]
  const spottingTypeLabel = 'views.fobs.spotting'
  const tooltipZ = tooltip?.z
  if (tooltipZ) {
    tooltipZ.formatter = (value: number) => {
      const spottingTypesTranslated = spottingTypesForTranslate.map((x) => t(x))
      return spottingTypesTranslated[value - 1]
    }
    tooltipZ.title = t(spottingTypeLabel) + ':'
  }
}

function insertNullElements() {
  let firstDate = 0
  let lastDate = 0
  seriesData.value.forEach((serie, serieIndex) => {
    serie.data.forEach((point: FobsPointData, pointIndex: number) => {
      const currentDate: number = toNumber(point[0]) ?? 0
      if (serieIndex === 0 && pointIndex === 0) {
        firstDate = currentDate
        lastDate = currentDate
      }
      if (currentDate < firstDate) firstDate = currentDate
      if (currentDate > lastDate) lastDate = currentDate
    })
  })
  // INFO: When the serie has some points, sometimes, the outer values are not shown.
  // INFO: To avoid it, we insert two null values at the outermost positions
  const margin: number = Math.floor((lastDate - firstDate) / 15) + 1000
  seriesData.value[0].data.push([firstDate - margin, null], [lastDate + margin, null])
}

onBeforeMount(() => {
  nextTick(() => {
    fullFillFobSeriesData(incidentFobs.value)
  })
})

watch(incidentFobs, (fobs: Array<IncidentFob>) => {
  nextTick(() => {
    fullFillFobSeriesData(fobs)
  })
})

watch(lang, () => {
  nextTick(() => {
    updateLanguage()
  })
})
</script>

<style scoped lang="scss">
.fobs_graph_container {
  background-color: rgb(var(--v-theme-background));
  border-radius: 5px;
}

.fobs_chart {
  width: 100% !important;
  padding-bottom: 20px;
}
</style>
